/* =========================================================
   SPRUCE HYGIENE — theme.js (Shopify OS 2.0)
   Delegated listeners so everything keeps working after
   section re-renders in the theme editor.
   ========================================================= */
(function () {
  'use strict';

  /* Run once. If theme.js ends up included twice on a page, a second run would
     register the delegated listeners again and every click would fire twice —
     most visibly the qty stepper jumping 1 -> 3 -> 5. */
  if (window.__spruceThemeInit) return;
  window.__spruceThemeInit = true;

  var money = window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : '';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function formatMoney(cents) {
    try { return '$' + (cents / 100).toFixed(2); } catch (e) { return ''; }
  }

  /* =====================================================================
     STOCK CEILING

     The reported fault: stock was 10 and the box happily took 17.

     Nothing in the theme knew what 10 was. The page carries
     product.variants | json for price and image switching, and Shopify does
     not put inventory_quantity in that JSON - only inventory_management. So
     the quantity box had no number to stop at, and the only thing standing
     between a customer and an impossible order was whatever Shopify decided
     at /cart/add.js, by which point they have already pressed the button.

     snippets/stock-data.liquid now writes the real figures into data-stock
     attributes. This reads them.

     TWO KINDS OF BOX, AND THEY DO NOT HAVE THE SAME CEILING

     A box on a product page or a product card says "add this many MORE", so
     its ceiling is stock minus whatever is already in the cart. Ten in the
     cart and ten in stock means that box may not go above zero.

     A box on a cart line says "make the total this many", so its ceiling is
     the stock figure itself. Those are marked data-qty-is-total.

     Getting this backwards is the classic version of this bug: cap both at
     the stock figure and a customer adds ten, then adds ten again.

     WHY THE CAP DOES NOT CONSULT inventory_policy

     "Continue selling when out of stock" is a Shopify setting that permits
     overselling. Respecting it here would mean the cap silently does nothing
     on exactly the products it is set on, which is not what was asked for.
     So the cap applies wherever stock is tracked, and the way to allow
     overselling again is the theme setting - Settings → Cart → "Limit
     quantity to available stock" - which is visible, rather than a per
     variant checkbox nobody remembers ticking.
     ===================================================================== */

  var stock = {};        /* variant id (string) -> { q, t, p } */
  var inCart = {};       /* variant id (string) -> units already in the cart */
  var cartKnown = false; /* false until /cart.js has answered once */

  function limitEnabled() {
    return document.body.getAttribute('data-limit-stock') !== 'false';
  }

  /* Pull every data-stock on the page (or a fragment) into the registry. */
  function registerStock(scope) {
    (scope || document).querySelectorAll('[data-stock]').forEach(function (el) {
      var raw = el.getAttribute('data-stock');
      if (!raw) return;
      var data;
      try { data = JSON.parse(raw); } catch (e) { return; }
      Object.keys(data).forEach(function (id) { stock[id] = data[id]; });
    });
  }

  /* The variant a quantity box belongs to. A product card and a cart line say
     so outright; on a product page it is whichever variant the form is
     currently pointed at, which changes as the options change. */
  function variantOf(input) {
    var explicit = input.getAttribute('data-qty-variant');
    if (explicit) return explicit;
    var root = input.closest('form') || input.closest('[data-variants]');
    var idField = root ? root.querySelector('[name="id"]') : null;
    return idField ? idField.value : null;
  }

  /**
   * How high this box may go, or null when nothing should stop it.
   * null means "untracked, or the cap is switched off" - not zero.
   */
  function ceilingFor(input) {
    if (!limitEnabled()) return null;
    var id = variantOf(input);
    if (!id) return null;
    var s = stock[String(id)];
    if (!s || !s.t) return null;            /* Shopify is not counting this one */

    var onHand = Math.max(0, s.q);
    if (input.hasAttribute('data-qty-is-total')) return onHand;

    /* An "add this many more" box. Subtract what is in the cart already -
       but only once the cart has actually answered, because treating an
       unknown cart as empty would let the first add through at full stock. */
    var held = cartKnown ? (inCart[String(id)] || 0) : 0;
    return Math.max(0, onHand - held);
  }

  /**
   * Hold a box within its ceiling. Returns true when it had to pull the
   * number down, so the caller can say why.
   */
  function clampQty(input, announce) {
    var max = ceilingFor(input);
    if (max === null) { input.removeAttribute('max'); return false; }

    input.setAttribute('max', String(max));

    var min = parseInt(input.getAttribute('min') || '1', 10);
    var val = parseInt(input.value, 10);
    if (isNaN(val)) return false;           /* mid-typing or empty; leave it */

    /* min is raised first and the ceiling applied second, so the ceiling
       wins. The other order lets a min of 1 push the value back up to 1 on a
       variant with nothing left to give, which is the bug being fixed
       reappearing one unit at a time. */
    var capped = val;
    if (capped < min) capped = min;
    if (capped > max) capped = max;
    if (capped === val) return false;

    input.value = String(capped);
    if (announce) {
      var id = variantOf(input);
      var s = id ? stock[String(id)] : null;
      var onHand = s ? Math.max(0, s.q) : max;
      toast(max === 0
        ? 'You already have all ' + onHand + ' we have in stock in your cart'
        : 'Only ' + onHand + ' in stock' +
          (max < onHand ? ' — ' + (onHand - max) + ' already in your cart' : ''), true);
    }
    return true;
  }

  /* Re-apply every ceiling on the page. Called after the cart changes and
     after a variant switch, because both move the ceiling. */
  function refreshQtyLimits(scope) {
    (scope || document).querySelectorAll('[data-qty-limited]').forEach(function (input) {
      clampQty(input, false);
    });
  }

  /* ---------- Toasts ---------- */
  function toastHost() {
    var h = document.querySelector('.toasts');
    if (!h) { h = document.createElement('div'); h.className = 'toasts'; document.body.appendChild(h); }
    return h;
  }
  function toast(msg, isError) {
    var host = toastHost();
    var t = document.createElement('div');
    t.className = 'toast' + (isError ? ' toast--error' : '');
    t.innerHTML = (isError ? '' : '<svg><use href="#i-check"/></svg>') + '<span>' + esc(msg) + '</span>';
    host.appendChild(t);
    setTimeout(function () {
      t.style.opacity = '0';
      t.style.transform = 'translateY(10px)';
      setTimeout(function () { t.remove(); }, 300);
    }, 2600);
  }
  window.spruceToast = toast;

  /* ---------- Mobile drawer ---------- */
  function initDrawer() {
    var btn = document.getElementById('nav-toggle');
    var menu = document.getElementById('mobile-nav');
    var overlay = document.getElementById('nav-overlay');
    var closeBtn = document.getElementById('drawer-close');
    if (!btn || !menu) return;
    function close() {
      menu.classList.remove('open'); btn.classList.remove('open');
      if (overlay) overlay.classList.remove('show');
      btn.setAttribute('aria-expanded', 'false'); document.body.classList.remove('nav-locked');
    }
    function open() {
      menu.classList.add('open'); btn.classList.add('open');
      if (overlay) overlay.classList.add('show');
      btn.setAttribute('aria-expanded', 'true'); document.body.classList.add('nav-locked');
    }
    btn.addEventListener('click', function () { menu.classList.contains('open') ? close() : open(); });
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (overlay) overlay.addEventListener('click', close);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
    menu.querySelectorAll('a').forEach(function (a) { a.addEventListener('click', close); });
    window.addEventListener('resize', function () { if (window.innerWidth > 860) close(); });
    menu.querySelectorAll('.m-group-toggle').forEach(function (t) {
      t.addEventListener('click', function () {
        var group = t.parentElement;
        var isOpen = group.classList.toggle('open');
        t.setAttribute('aria-expanded', String(isOpen));
        menu.querySelectorAll('.m-group').forEach(function (g) {
          if (g !== group) { g.classList.remove('open'); var mt = g.querySelector('.m-group-toggle'); if (mt) mt.setAttribute('aria-expanded', 'false'); }
        });
      });
    });
  }

  /* ---------- Scroll reveal ---------- */
  function initReveal() {
    if (!('IntersectionObserver' in window)) { document.documentElement.classList.add('no-observer'); return; }
    var els = document.querySelectorAll('[data-reveal], .reveal');
    els.forEach(function (el, i) { el.classList.add('reveal'); el.style.setProperty('--rd', (i % 6) * 0.09 + 's'); });
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Cart drawer open/close ---------- */
  function openCartDrawer() {
    var d = document.querySelector('.cart-drawer'); var o = document.querySelector('.cart-overlay');
    if (d) d.classList.add('open'); if (o) o.classList.add('show');
  }
  function closeCartDrawer() {
    var d = document.querySelector('.cart-drawer'); var o = document.querySelector('.cart-overlay');
    if (d) d.classList.remove('open'); if (o) o.classList.remove('show');
  }

  /* ---------- Refresh cart count + mini cart ---------- */
  /* Put every cart-line quantity box back to what the cart actually holds.
     Used after a refused change: the box still shows the number the customer
     typed, and leaving it there invites them to press the same button again
     and get the same refusal. */
  function syncLineInputs(cart) {
    var byKey = {};
    (cart.items || []).forEach(function (it) { byKey[String(it.key)] = it.quantity; });

    document.querySelectorAll('[data-cart-qty]').forEach(function (input) {
      var k = String(input.getAttribute('data-cart-qty'));

      /* An outstanding change is the customer's current intent; resetting it
         to the cart would undo a click they have just made. */
      if (Object.prototype.hasOwnProperty.call(lineWanted, k)) return;

      if (Object.prototype.hasOwnProperty.call(byKey, k)) {
        input.value = String(byKey[k]);
      }
    });
  }

  /**
   * Re-read the cart and bring the badge, the drawer and the stock ceilings
   * back in step with it.
   *
   * @param {boolean} [syncInputs] Also reset the cart-line quantity boxes to
   *   what the cart holds. OFF by default, and deliberately so: this response
   *   is asynchronous, and on boot it can land after the customer has already
   *   pressed + - at which point writing the server's number back undoes
   *   their click and the button looks broken. Only the refused-change path
   *   wants it, because there the box is showing a number the cart rejected.
   */
  function refreshCart(syncInputs) {
    /* Noted before asking: if a write is already outstanding, or one starts
       while this is in the air, the answer may describe the cart as it was
       before that write and must not be painted. The write's own answer will
       paint the correct cart a moment later. */
    var overlapped = writesInFlight > 0;
    var writesAtStart = writeSeq;

    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (overlapped || writeSeq !== writesAtStart || writesInFlight > 0) return;

        /* Same reasoning as changeLine: only a real cart has items. A
           throttle response or an error page must not be allowed to blank
           the drawer and wipe the tally. */
        if (!cart || !cart.items) return;

        document.querySelectorAll('.cart-count').forEach(function (el) { el.textContent = cart.item_count; });
        noteCartQuantities(cart);
        renderMiniCart(cart);
        if (syncInputs) syncLineInputs(cart);
        /* The cart moved, so every "add this many more" ceiling moved with it. */
        refreshQtyLimits();
      }).catch(function () {});
  }

  /* What the cart holds of each variant, so the product page can subtract it.
     Several lines can share a variant - the same thing added with different
     line properties - so they are summed rather than overwritten. */
  function noteCartQuantities(cart) {
    inCart = {};
    (cart.items || []).forEach(function (it) {
      var k = String(it.variant_id);
      inCart[k] = (inCart[k] || 0) + it.quantity;
    });
    cartKnown = true;
  }
  /* The quantity to print in a line's box: what the customer asked for while
     that is outstanding, otherwise what the cart holds. */
  function wantedFor(it) {
    return Object.prototype.hasOwnProperty.call(lineWanted, String(it.key))
      ? lineWanted[String(it.key)]
      : it.quantity;
  }

  function renderMiniCart(cart) {
    var hosts = document.querySelectorAll('[data-mini-cart]');
    if (!hosts.length) return;
    var html;
    if (!cart.items || !cart.items.length) {
      html = '<p class="mini-cart__empty">Your cart is empty.</p>';
    } else {
      html = '<div class="mini-cart__list">';
      cart.items.forEach(function (it) {
        /* The chosen option, as a chip rather than another line of grey text.
           A product card adds the first available variant without asking, so
           the drawer is the first place the shopper - and later the person
           packing the order - can see WHICH one went in.

           It needs to stand out from the title, not blend into it: these
           product names already contain both sizes ("... 3Ply 24 & 48
           Rolls"), so a quiet sub-line reads as more title rather than as
           the choice that was made.

           Skipped for products with no real options, where Shopify names the
           only variant "Default Title" and printing it would be noise. */
        var variant = '';
        if (!it.product_has_only_default_variant && it.variant_title && it.variant_title !== 'Default Title') {
          variant = '<div class="mc-option">' + esc(it.variant_title) + '</div>';
        }

        /* Anything added as a line item property, shown the way the cart page
           shows it, so the two cannot disagree. */
        var props = '';
        if (it.properties) {
          Object.keys(it.properties).forEach(function (k) {
            var v = it.properties[k];
            if (v === null || v === '' || k.charAt(0) === '_') { return; }
            props += '<div class="m">' + esc(k) + ': ' + esc(String(v)) + '</div>';
          });
        }

        /* Quantity in words as well as in the stepper. The stepper is a
           control - it invites a change and is easy to read past - whereas
           "Qty 2 x $18.88" is a statement of what is in the cart, which is
           what was actually being asked for. */
        var unit = '<div class="mc-meta">Qty ' + it.quantity + ' &times; ' + formatMoney(it.final_price) + '</div>';

        html += '<div class="mini-cart__row" data-key="' + esc(it.key) + '">' +
          '<img src="' + esc(it.image ? it.image : '') + '" alt="' + esc(it.product_title) + '">' +
          '<div class="mc-info">' +
            '<div class="t">' + esc(it.product_title) + '</div>' +
            variant +
            props +
            unit +
            '<div class="mc-line">' +
              '<div class="qty mini-qty">' +
                '<button type="button" data-qty-step="down" aria-label="Decrease quantity">&minus;</button>' +
                /* data-qty-is-total: a drawer line is the whole amount of that
                   variant, so it caps at the stock figure, not at what is
                   left over. The variant's stock is only known here if
                   something else on the page printed it, and ceilingFor
                   returns null when it is not - the drawer simply does not
                   cap, and the cart page, which always knows, does. */
                '<input type="number" data-cart-qty="' + esc(it.key) + '" value="' + wantedFor(it) +
                '" min="0" inputmode="numeric" data-qty-limited data-qty-is-total' +
                ' data-qty-variant="' + esc(it.variant_id) + '" aria-label="Quantity">' +
                '<button type="button" data-qty-step="up" aria-label="Increase quantity">+</button>' +
              '</div>' +
              '<span class="mc-price">' + formatMoney(it.final_line_price) + '</span>' +
            '</div>' +
          '</div>' +
          '<button class="rm" data-cart-remove="' + esc(it.key) + '" aria-label="Remove">&times;</button>' +
          '</div>';
      });
      html += '</div><div class="mini-cart__foot"><span>Subtotal</span><span>' + formatMoney(cart.total_price) + '</span></div>' +
        '<a class="btn btn-gold" href="/cart" style="width:100%;justify-content:center">View cart &amp; checkout</a>';
    }
    hosts.forEach(function (h) { h.innerHTML = html; });
  }

  /* ---------- AJAX add to cart ---------- */
  function addToCart(form, btn) {
    var data = new FormData(form);
    var label = btn ? btn.innerHTML : '';
    if (btn) { btn.setAttribute('disabled', 'true'); btn.innerHTML = 'Adding…'; }
    fetch('/cart/add.js', { method: 'POST', headers: { 'Accept': 'application/json' }, body: data })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
      .then(function (res) {
        if (!res.ok) { toast(res.body && res.body.description ? res.body.description : 'Could not add to cart', true); }
        else { toast((res.body.product_title || res.body.title || 'Item') + ' added to your cart'); refreshCart(); openCartDrawer(); }
      })
      .catch(function () { toast('Could not add to cart', true); })
      .finally(function () { if (btn) { btn.removeAttribute('disabled'); btn.innerHTML = label; } });
  }

  /* ---------- Change / remove line ---------- */
  /* Pending timer and latest request number, per cart line. */
  var lineTimers = {};
  var lineSeq = {};

  /* What the customer has ASKED FOR, per line, while that is still
     outstanding. A cart answer is the truth about the cart, but not about the
     quantity box: between the click and the acknowledgement the box belongs to
     the customer, and anything that repaints it has to read from here instead.
     Cleared once the cart comes back agreeing, so it is only ever a short-
     lived override and never a second source of truth. */
  var lineWanted = {};

  /* Cart WRITES, counted. A read that overlaps a write cannot be trusted to
     be current, however late it was issued - the server may have answered it
     before the write landed. */
  var writeSeq = 0;        /* bumped when a write is sent */
  var writesInFlight = 0;  /* writes sent but not yet answered */

  /* How long a burst of clicks is allowed to settle before anything is sent.
     Long enough to collapse a run of + presses, short enough that a single
     click still feels immediate. */
  var LINE_DEBOUNCE_MS = 300;

  /**
   * Change a cart line.
   *
   * @param {string}  key       The line key.
   * @param {number}  qty       The quantity wanted.
   * @param {boolean} [now]     Send straight away rather than waiting for the
   *   burst to settle. Used by Remove, where any delay reads as a dead button.
   */
  function changeLine(key, qty, now) {
    clearTimeout(lineTimers[key]);

    /* Recorded here rather than in sendLineChange, because the whole point is
       to cover the gap BEFORE the request goes out. */
    lineWanted[key] = qty;

    if (now) { sendLineChange(key, qty); return; }

    lineTimers[key] = setTimeout(function () {
      sendLineChange(key, qty);
    }, LINE_DEBOUNCE_MS);
  }

  function sendLineChange(key, qty) {
    /* Claim a number before asking. Any answer that comes back carrying an
       older one has been overtaken and must not be allowed to paint. */
    var seq = (lineSeq[key] = (lineSeq[key] || 0) + 1);

    writeSeq++;
    writesInFlight++;

    /* Called the moment an answer arrives, success or failure, and only once.
       The final .then below is a backstop for paths that never get here. */
    var released = false;
    function release() {
      if (released) return;
      released = true;
      writesInFlight = Math.max(0, writesInFlight - 1);
    }

    fetch('/cart/change.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(function (r) {
      release();
      return r.json().then(function (body) { return { ok: r.ok, body: body }; });
    }).then(function (res) {
      /* Overtaken: a later change for this line has already been sent, so
         this answer describes a cart that is one step out of date. Painting
         it would show the customer a number they have already moved past. */
      if (seq !== lineSeq[key]) return;

      /*
       * A REFUSED CHANGE IS NOT A CART.
       *
       * Ask for more than Shopify has left and it answers 422 with
       * { status, message, description } - no items, no item_count. This used
       * to be passed straight on as though it were a cart, which printed
       * "Your cart is empty" over a cart that was still full, blanked the
       * badge, and wiped the quantity tally the stock ceilings are worked out
       * from. The cart on the server was never touched, which is why the
       * items reappeared on the next page load.
       *
       * The client clamps quantities to the stock it knows about, so this is
       * normally unreachable - but the figure it clamps against was rendered
       * when the page loaded. If someone else buys the last few, or the stock
       * is lowered in the admin, the clamp is generous by a unit or two and
       * the request is refused. That race cannot be designed away, so the
       * refusal has to be handled rather than assumed not to happen.
       */
      if (!res.ok || !res.body || !res.body.items) {
        /* Refused, so there is nothing outstanding to protect any more - and
           the resync below is the one case where overwriting the box is what
           the customer wants, since it holds a number Shopify rejected. */
        if (lineWanted[key] === qty) delete lineWanted[key];
        toast(res.body && res.body.description ? res.body.description : 'Could not update the cart', true);
        /* true: the box is showing the number Shopify just refused, so this
           is the one case where resetting it is what the customer wants. */
        refreshCart(true);
        return;
      }

      /* Only when the cart has caught up with the latest ask. If a newer
         figure has been recorded since, it is still outstanding and the box
         must keep showing it. */
      if (lineWanted[key] === qty) delete lineWanted[key];

      var cart = res.body;
      document.querySelectorAll('.cart-count').forEach(function (el) { el.textContent = cart.item_count; });
      /* Changing a line changes what the cart holds, so the "add this many
         more" ceiling on any product box behind the drawer moves too. Without
         this, taking six out of the cart left the product page still believing
         six were in it, and refusing a quantity it should have allowed. */
      noteCartQuantities(cart);
      renderMiniCart(cart);
      refreshQtyLimits();
      if (document.querySelector('[data-cart-page]')) window.location.reload();
    }).catch(function () {
      /* The request never arrived anywhere. Holding the ask open would leave
         the box permanently out of step with the cart and immune to resync. */
      if (lineWanted[key] === qty) delete lineWanted[key];
      release();
    }).then(release);
  }

  /* ---------- Delegated click handling ---------- */
  document.addEventListener('click', function (e) {
    var t = e.target;

    var cartOpen = t.closest('[data-cart-open]');
    if (cartOpen && document.querySelector('.cart-drawer')) { e.preventDefault(); refreshCart(); openCartDrawer(); return; }
    if (t.closest('[data-cart-close]') || t.closest('.cart-overlay')) { closeCartDrawer(); return; }

    var srch = t.closest('.header-search__toggle');
    if (srch) { srch.closest('.header-search').classList.toggle('open'); return; }

    var qStep = t.closest('[data-qty-step]');
    if (qStep) {
      /* Guard against the same click being processed twice (e.g. a duplicate
         listener): both listeners see the same event object, so a flag on it
         stops a second increment. */
      if (e.__qtyStepped) return;
      e.__qtyStepped = true;
      var wrap = qStep.closest('.qty'); var input = wrap.querySelector('input');
      var min = parseInt(input.min || '1', 10);
      var up = qStep.dataset.qtyStep === 'up';
      var val = (parseInt(input.value, 10) || min) + (up ? 1 : -1);

      /* Stop the + at the stock level rather than letting it run up and get
         pulled back, which reads as the button being broken. */
      var max = ceilingFor(input);
      if (up && max !== null && val > max) {
        /* Write the over-the-line value and let clampQty pull it back, so the
           customer is told why the + stopped. Setting it to max here instead
           would leave clampQty with nothing to correct and nothing to say. */
        input.value = String(val);
        clampQty(input, true);
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      input.value = val < min ? min : val; input.dispatchEvent(new Event('change', { bubbles: true })); return;
    }

    /* Service plan cards: frequency pills drive the hidden variant select */
    var freqBtn = t.closest('.plan-freq__btn');
    if (freqBtn) {
      e.preventDefault();
      var group = freqBtn.parentElement;
      group.querySelectorAll('.plan-freq__btn').forEach(function (b) { b.classList.toggle('is-active', b === freqBtn); });
      var gIndex = group.getAttribute('data-freq-group');
      var vRoot = freqBtn.closest('[data-variants]');
      if (vRoot && gIndex !== null) {
        var sel = vRoot.querySelector('[data-option-index="' + gIndex + '"]');
        if (sel) { sel.value = freqBtn.getAttribute('data-freq'); sel.dispatchEvent(new Event('change', { bubbles: true })); }
      }
      return;
    }

    var acc = t.closest('[data-accordion]');
    if (acc) {
      var expanded = acc.getAttribute('aria-expanded') === 'true';
      acc.setAttribute('aria-expanded', String(!expanded));
      var panel = acc.nextElementSibling; if (panel) panel.hidden = expanded; return;
    }

    var thumb = t.closest('[data-thumb]');
    if (thumb) {
      showGalleryThumb(thumb);
      return;
    }

    var cfg = t.closest('.cfg-opt');
    if (cfg && !cfg.querySelector('input')) {
      cfg.parentElement.querySelectorAll('.cfg-opt').forEach(function (o) { o.classList.toggle('is-active', o === cfg); }); return;
    }

    var tab = t.closest('[data-tab]');
    if (tab) {
      var tabs = tab.closest('.tabs'); var name = tab.dataset.tab;
      tabs.querySelectorAll('[data-tab]').forEach(function (x) { x.setAttribute('aria-selected', String(x === tab)); });
      tabs.querySelectorAll('[data-panel]').forEach(function (p) { p.hidden = p.dataset.panel !== name; });
      return;
    }

    var filterToggle = t.closest('[data-filter-toggle]');
    if (filterToggle) { var side = document.querySelector('[data-filter-panel]'); if (side) side.classList.toggle('open'); return; }

    var copy = t.closest('[data-copy-link]');
    if (copy && navigator.clipboard) { navigator.clipboard.writeText(window.location.href); toast('Link copied'); return; }

    var rm = t.closest('[data-cart-remove]');
    if (rm) { e.preventDefault(); changeLine(rm.getAttribute('data-cart-remove'), 0, true); return; }
  });

  /* ---------- Add-to-cart form submit (delegated) ---------- */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[action*="/cart/add"], form[data-cart-form]');
    if (!form) return;

    /* The last gate before the request. Typing a number, pasting one, or
       using the browser's own spinner all reach here without the stepper
       having been touched, so the ceiling is applied once more. This runs
       even when the form is about to POST normally. */
    var qtyField = form.querySelector('[data-qty-limited]');
    if (qtyField) {
      clampQty(qtyField, true);
      if (parseInt(qtyField.value, 10) < 1) {
        e.preventDefault();
        return;   /* nothing left to add; clampQty has said so */
      }
    }

    if (!document.querySelector('.cart-drawer')) return; /* let it POST normally if no drawer */
    e.preventDefault();
    addToCart(form, form.querySelector('[type="submit"], .product-add, [data-add-btn]'));
  });

  /* ---------- Quick-add buttons (cards) ---------- */
  document.addEventListener('click', function (e) {
    var qa = e.target.closest('[data-quick-add]');
    if (!qa) return;
    e.preventDefault();
    var id = qa.getAttribute('data-quick-add');
    if (!id) return;
    /* Read the quantity chosen on this card's stepper (defaults to 1). */
    var card = qa.closest('.product-card') || qa.closest('.product-foot') || qa.parentElement;
    var qInput = card ? card.querySelector('.qty input') : null;
    if (qInput) {
      clampQty(qInput, true);
      if (parseInt(qInput.value, 10) < 1) return;  /* none left to add */
    }
    var qty = qInput ? (parseInt(qInput.value, 10) || 1) : 1;
    if (qty < 1) { qty = 1; }
    var tmp = document.createElement('form');
    tmp.innerHTML = '<input name="id" value="' + esc(id) + '"><input name="quantity" value="' + qty + '">';
    addToCart(tmp, qa);
    if (qInput) { qInput.value = 1; }
  });

  /* ---------- Collection filters: auto-submit on change ---------- */
  document.addEventListener('change', function (e) {
    /* A typed or pasted number lands here. Catch it before it becomes a
       request, on a product box as well as a cart line. */
    var limited = e.target.closest('[data-qty-limited]');
    if (limited) clampQty(limited, true);

    var cartQty = e.target.closest('[data-cart-qty]');
    if (cartQty) { changeLine(cartQty.getAttribute('data-cart-qty'), parseInt(cartQty.value, 10) || 0); return; }
    var facet = e.target.closest('[data-auto-submit]');
    if (facet) { var f = facet.closest('form'); if (f) f.submit(); }
  });

  /* ---------- PDP gallery ----------
     One place that knows how to show an image, used by a thumbnail click and
     by a variant change. Two copies of this would drift, and the part that
     drifts is the srcset — see below. */
  function showGalleryThumb(thumb) {
    if (!thumb) return;

    var gallery = thumb.closest('.pdp-gallery');
    if (!gallery) return;

    var main = gallery.querySelector('.pdp-main img');

    if (main) {
      /* Swap to the LARGE rendition the thumb carries, never the thumb's own
         160px file. srcset must be set too: the browser prefers srcset over
         src, so updating src alone leaves the old image on screen. Falls
         back to the thumbnail only if a theme copy predates data-full. */
      var full = thumb.getAttribute('data-full');
      var fullSet = thumb.getAttribute('data-full-srcset');
      var fullAlt = thumb.getAttribute('data-full-alt');
      if (fullSet) { main.srcset = fullSet; } else { main.removeAttribute('srcset'); }
      main.src = full || thumb.querySelector('img').src;
      if (fullAlt !== null) { main.alt = fullAlt; }
      var id = thumb.getAttribute('data-image-id');
      if (id) { main.setAttribute('data-image-id', id); }
    }

    gallery.querySelectorAll('[data-thumb]').forEach(function (x) {
      x.setAttribute('aria-current', String(x === thumb));
    });
  }

  /* The variant's own photo, shown by pointing at its thumbnail where there is
     one. A product can have more images than the six the gallery prints, so a
     variant whose photo is not among them is shown straight from the variant
     data instead of being silently ignored. */
  function showVariantImage(root, variant) {
    if (!variant) return;

    var pdp = root.closest('.pdp') || root.parentNode;
    var gallery = pdp && pdp.querySelector ? pdp.querySelector('.pdp-gallery') : null;
    if (!gallery) return;

    var image = variant.featured_image;
    if (!image) return;

    var main = gallery.querySelector('.pdp-main img');
    if (!main) return;

    /* Already showing it — leave the DOM alone rather than reloading the same
       file and dropping the browser's decode. */
    if (String(main.getAttribute('data-image-id')) === String(image.id)) return;

    var thumb = gallery.querySelector('[data-thumb][data-image-id="' + image.id + '"]');

    if (thumb) { showGalleryThumb(thumb); return; }

    /* Beyond the printed thumbnails. src alone would lose to the srcset still
       on the element, so that is cleared first. */
    main.removeAttribute('srcset');
    main.src = image.src;
    main.setAttribute('data-image-id', image.id);
    if (image.alt) { main.alt = image.alt; }

    gallery.querySelectorAll('[data-thumb]').forEach(function (x) {
      x.setAttribute('aria-current', 'false');
    });
  }

  /* ---------- PDP variant selection ---------- */
  function initVariants(scope) {
    (scope || document).querySelectorAll('[data-variants]').forEach(function (root) {
      var data = JSON.parse(root.getAttribute('data-variants') || '[]');
      var idInput = root.querySelector('[name="id"]');
      var priceEl = root.querySelector('[data-price]');
      var addBtn = root.querySelector('[data-add-btn]');
      function selectedOptions() {
        return Array.prototype.map.call(root.querySelectorAll('[data-option-index]'), function (sel) { return sel.value; });
      }
      function update() {
        var opts = selectedOptions();
        var match = data.find(function (v) { return v.options.every(function (o, i) { return o === opts[i]; }); });
        if (!match) return;
        if (idInput) idInput.value = match.id;
        if (priceEl) priceEl.textContent = formatMoney(match.price);

        /* Switching variant switches the stock ceiling with it. 10 of the 1L
           says nothing about the 1.2L, and a quantity left over from the
           previous choice has to be brought inside the new one's limit. */
        refreshQtyLimits(root);

        /* The reported fault: the photo never followed the option. */
        showVariantImage(root, match);

        /* Compare-at belongs to the variant. With one size discounted and
           another not, these were left showing the first one's saving. */
        var onSale = match.compare_at_price && match.compare_at_price > match.price;
        var compareEl = root.querySelector('[data-compare-price]');
        var saveEl = root.querySelector('[data-save]');

        if (compareEl) {
          compareEl.textContent = onSale ? formatMoney(match.compare_at_price) : '';
          compareEl.hidden = !onSale;
        }

        if (saveEl) {
          saveEl.textContent = onSale
            ? 'Save ' + Math.round((match.compare_at_price - match.price) * 100 / match.compare_at_price) + '%'
            : '';
          saveEl.hidden = !onSale;
        }

        /* So a refresh, a share or the back button keeps the chosen variant. */
        if (window.history && window.history.replaceState && root.dataset.variantsBound) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', match.id);
          window.history.replaceState({}, '', url.toString());
        }
        if (addBtn) {
          /* Sections can supply their own wording and keep inner markup (e.g. an arrow icon) */
          var labelEl = addBtn.querySelector('[data-add-label]') || addBtn;
          var addLabel = addBtn.getAttribute('data-label-add') || 'Add to cart';
          var outLabel = addBtn.getAttribute('data-label-soldout') || 'Sold out';
          if (match.available) { addBtn.disabled = false; addBtn.classList.remove('btn-soldout'); labelEl.textContent = addLabel; }
          else { addBtn.disabled = true; addBtn.classList.add('btn-soldout'); labelEl.textContent = outLabel; }
        }
      }
      root.querySelectorAll('[data-option-index]').forEach(function (sel) { sel.addEventListener('change', update); });

      /* Settle the button and the price for whatever is selected, then mark the
         root as bound: the flag is what stops that first call writing a
         ?variant= onto a URL the customer arrived at without one. */
      update();
      root.dataset.variantsBound = '1';
    });
  }

  /* ---------- Sticky add-to-cart bar ---------- */
  function initStickyBar() {
    var bar = document.querySelector('[data-pdp-sticky]');
    var anchor = document.querySelector('[data-add-btn]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { bar.classList.toggle('show', !en.isIntersecting); });
    }, { rootMargin: '0px 0px -40% 0px' });
    io.observe(anchor);
  }

  /* ---------- Pre-select "Service of interest" from ?service= ----------
     The shop's "Request a Quote" buttons link to /pages/contact with the
     category they came from, e.g. ?service=Washroom%20Consumables, so the
     customer does not have to find it in the list again.

     This has to be JavaScript: Shopify Liquid cannot read query parameters.
     Matching is case- and punctuation-insensitive so a link that says
     "washroom-consumables" still finds the "Washroom Consumables" option;
     an unrecognised value is ignored and the form keeps its default. */
  function initServicePreselect(scope) {
    var wanted;
    try {
      wanted = new URLSearchParams(window.location.search).get('service');
    } catch (e) { return; }
    if (!wanted) return;

    var norm = function (s) { return (s || '').toLowerCase().replace(/[^a-z0-9]/g, ''); };
    var target = norm(wanted);
    if (!target) return;

    (scope || document).querySelectorAll('[data-service-select]').forEach(function (sel) {
      for (var i = 0; i < sel.options.length; i++) {
        if (norm(sel.options[i].text) === target) { sel.selectedIndex = i; return; }
      }
    });
  }

  /* registerStock runs before initVariants, because the variant switcher
     settles the quantity ceiling on its first pass and needs the figures to
     already be in hand. refreshCart then arrives with what is in the cart and
     tightens the "add this many more" boxes a second time. */
  function boot() {
    registerStock();
    initDrawer(); initReveal(); initVariants(); initStickyBar(); initServicePreselect();
    refreshQtyLimits();
    refreshCart();
  }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  document.addEventListener('shopify:section:load', function (e) {
    initDrawer(); initReveal(); initVariants(e.target); initStickyBar(); initServicePreselect(e.target);
  });
})();
